import React, { useState, useEffect, useRef } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import { useNavigate } from "react-router";
import Response_SearchResult from "../../types/responses/Response.SearchResult.type";
import { debounce } from "lodash";
import StyledTextField from "../../components/mui/StyledTextField";
import SearchResult from "../../types/SearchResult.type";
import { Paper } from "@mui/material";
import { searchAsYouType } from "../../commons/Api";
import SearchIcon from "@mui/icons-material/Search";
import InputAdornment from "@mui/material/InputAdornment";
import { stripHtml, toPrettyTermName } from "../../commons/LaUtils";
import { HTMLAttributes } from "react";
import classes from "./Searchbox.module.css";
import LaButton from "../../components/button/LaButton.component";
import { BiSearch } from "react-icons/bi";
import { COLOR_NEUTRAL } from "../../commons/LaColors";

interface SearchBoxProps {
  onCloseHandler?: () => void;
}

// managing state
export default function SearchBox({ onCloseHandler }: SearchBoxProps) {
  const SEARCH_MIN_LENGTH = 2;
  const SEARCH_RESULT_LIMIT = 10;
  const DEBOUNCE_MS = 250;

  const [open, setOpen] = useState(false);
  const [show, setShow] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selectedValue, setSelectedValue] = useState<
    SearchResult | string | null
  >(null);
  const [activeQuery, setActiveQuery] = useState("");

  const [options, setOptions] = useState<readonly SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [showAllResultButton, setShowAllResultButton] = useState<boolean>(false);
  const latestRequestId = useRef(0);

  const resetSearchState = () => {
    debouncedSearch.cancel();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    setInputValue("");
    setSelectedValue(null);
    setActiveQuery("");
    setOptions([]);
    setShowAllResultButton(false);
    setOpen(false);
  };

  const formatOptionLabel = (option: SearchResult | string) => {
    if (typeof option === "string") {
      return option;
    }

    if (option.elementType === "Definitions") {
      return "(" + option.termName + ") " + option.matchedText.substring(0, 20) + "...";
    }

    return option.termName;
  };

  const OtherResults = (props: HTMLAttributes<HTMLElement>) => {
    return (
      <div>
        <Paper {...props}>
          {props.children}
          {!!options.length && showAllResultButton ? (
            <div className="px-4 my-3">
              <LaButton
                type="button"
                fullWidth
                onMouseDown={(event) => {
                  event.preventDefault();
                  const query = activeQuery || inputValue.trim();
                  if (!query) {
                    return;
                  }
                  navigate("/search/" + query);
                  resetSearchState();
                  onCloseHandler && onCloseHandler();
                }}
                laStyle="btn-navy"
              >
                Display all results
              </LaButton>
            </div>
          ) : null}
        </Paper>
      </div>
    );
  };

  useEffect(() => {
    if (!open) {
      setOptions([]);
    }
  }, [open]);

  const getResults = (value: string) => {
    const requestId = ++latestRequestId.current;
    setLoading(true);
    searchAsYouType(value, SEARCH_RESULT_LIMIT)
      .then((response) => {
        if (requestId !== latestRequestId.current) {
          return;
        }

        var searchdata: Response_SearchResult = response.data;
        var searchResults: SearchResult[] = [];
        searchdata.terms.forEach(
          (t) =>
            searchResults.push({
              id: t.id,
              termName: t.name,
              matchedText: t.name,
              elementType: "Terms",
              path: "/term/" + t.id + "/" + toPrettyTermName(t.name),
            }),
        );
        searchdata.definitions.forEach((d) => {
          searchResults.push({
            id: d.definition.id,
            termName: d.definition.termName,
            matchedText: stripHtml(d.definition.content),
            elementType: "Definitions",
            path: "/definition/" + d.definition.id,
          });
        });

        setOptions(searchResults);
      })
      .catch((error) => {
        // blank
      })
      .finally(() => {
        if (requestId === latestRequestId.current) {
          setLoading(false);
        }
      });
  };

  const onChangeHandler = (value: SearchResult | string | null) => {
    setSelectedValue(value);

    if (value === null) {
      return;
    } else if (typeof value === "string") {
      const query = value.trim();
      if (!query) {
        return;
      }
      debouncedSearch.cancel();
      setActiveQuery(query);
      navigate("/search/" + query);
      onCloseHandler && onCloseHandler();
    } else {
      resetSearchState();
      navigate(value.path);
    }
  };

  const debouncedSearch = React.useRef(
    debounce((value: string) => {
      changeOptionBaseOnValue(value);
    }, DEBOUNCE_MS),
  ).current;

  const changeOptionBaseOnValue = (value: string) => {
    const query = value.trim();
    if (query.length >= SEARCH_MIN_LENGTH) {
      setActiveQuery(query);
      getResults(query);
      setShowAllResultButton(true);
    } else {
      latestRequestId.current += 1;
      setLoading(false);
      setActiveQuery("");
      setOptions([]);
      setShowAllResultButton(false);
    }
  };

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // Rendering our parameters on the DOM
  return (
    <>
      <div
        className={
          !show
            ? classes.searchbox
            : classes.searchbox + " " + classes.showsearchbox
        }
      >
        <Autocomplete
          className={classes.AutocompleteSearchbox}
          PaperComponent={OtherResults}
          freeSolo
          noOptionsText={"Empty results"}
          size="small"
          id="termsanddefinitions"
          value={selectedValue}
          onChange={(event, newInputValue) => onChangeHandler(newInputValue)}
          inputValue={inputValue}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === "reset") {
              return;
            }

            if (reason === "clear") {
              resetSearchState();
              return;
            }

            setInputValue(newInputValue);
            debouncedSearch(newInputValue);
          }}
          open={open}
          onOpen={() => {
            setOpen(true);
            const query = inputValue.trim();
            if (query.length >= SEARCH_MIN_LENGTH) {
              changeOptionBaseOnValue(query);
            }
          }}
          onClose={() => {
            setOpen(false);
            onCloseHandler && onCloseHandler();
          }}
          isOptionEqualToValue={(option, value) => {
            if (typeof option === "string") {
              return option === value;
            }
            if (typeof value === "string") {
              return option.termName === value;
            }
            return option.matchedText === value.matchedText;
          }}
          getOptionLabel={formatOptionLabel}
          options={options}
          loading={loading}
          groupBy={(option) =>
            typeof option === "string" ? "Other" : option.elementType
          }
          renderOption={(props, option) => {
            if (typeof option === "string") {
              return (
                <div
                  style={{
                    fontSize: 14,
                  }}
                  key={option}
                >
                  <li {...props}>{option}</li>
                </div>
              );
            }

            return (
              <div
                // className="border-top"
                style={{
                  fontSize: 14,
                }}
                key={option.id}
              >
                <li {...props}>{formatOptionLabel(option)}</li>
              </div>
            );
          }}
          renderGroup={(params) => {
            return (
              <div className="px-4">
                <div
                  className=" text-secondary px-3 sticky-top bg-white"
                  style={{ top: "-8px", fontSize: "14px" }}
                >
                  <h6 className="border-bottom py-2">{params.group}</h6>
                </div>
                <div>{params.children}</div>
              </div>
            );
          }}
          filterOptions={(x) => x}
          renderInput={(params) => (
            <StyledTextField
              // sx={onCloseHandler && { border: 'none', "& fieldset": { border: 'none' }, }}
              {...params}
              autoFocus={!!onCloseHandler}
              placeholder="Search Lapedia"
              itemID={params.id}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <BiSearch color={COLOR_NEUTRAL} size={24} />
                  </InputAdornment>
                ),
                //disableUnderline: true,
                style: {
                  fontSize: 14,
                  border: "none",
                  paddingTop: 7,
                  paddingBottom: 7,
                },
              }}
            />
          )}
        />
      </div>
      <div className={classes.btnmobile}>
        <button className={classes.btnMobileNav} onClick={() => setShow(!show)}>
          <SearchIcon
            className={"icon " + classes.icon}
            sx={{ color: "action.active", mr: 1, my: 0.5 }}
          />
        </button>
      </div>
    </>
  );
}
